import React, { createContext, useContext, useEffect, useState } from 'react'
import { useAuthContext } from './AuthContext'
import { subscriptionService, adminService, userService } from '../services/firestore'
import { Subscription, AdminSettings, Trial } from '../types/user'
import { Timestamp } from 'firebase/firestore'

interface PaywallContextType {
  isPaywallEnabled: boolean
  isLoading: boolean
  userSubscription: Subscription | null
  trialDaysRemaining: number | null
  isTrialExpired: boolean
  isSubscriptionActive: boolean
  hasPaymentIssues: boolean
  gracePeriodDays: number | null
  checkSubscriptionStatus: () => Promise<void>
  refreshSubscription: () => Promise<void>
}

const PaywallContext = createContext<PaywallContextType | undefined>(undefined)

export const usePaywallContext = () => {
  const context = useContext(PaywallContext)
  if (context === undefined) {
    throw new Error('usePaywallContext must be used within a PaywallProvider')
  }
  return context
}

interface PaywallProviderProps {
  children: React.ReactNode
}

export const PaywallProvider: React.FC<PaywallProviderProps> = ({ children }) => {
  const { user, isAuthenticated } = useAuthContext()
  const [isPaywallEnabled, setIsPaywallEnabled] = useState(true)
  const [isLoading, setIsLoading] = useState(true)
  const [userSubscription, setUserSubscription] = useState<Subscription | null>(null)
  const [trialDaysRemaining, setTrialDaysRemaining] = useState<number | null>(null)
  const [isTrialExpired, setIsTrialExpired] = useState(false)
  const [isSubscriptionActive, setIsSubscriptionActive] = useState(false)
  const [hasPaymentIssues, setHasPaymentIssues] = useState(false)
  const [gracePeriodDays, setGracePeriodDays] = useState<number | null>(null)

  const checkSubscriptionStatus = async () => {
    // Don't set loading to false if user isn't ready - keep waiting
    if (!user?.uid) {
      // Keep isLoading as true to prevent guards from making decisions on incomplete data
      return
    }

    try {
      // Create a single timestamp for all calculations to ensure consistency
      const now = new Date()
      
      // Check admin settings for paywall status
      const adminSettings = await adminService.getAdminSettings()
      setIsPaywallEnabled(adminSettings?.paywallEnabled ?? true)

      // If paywall is disabled, allow access
      if (!adminSettings?.paywallEnabled) {
        setIsSubscriptionActive(true)
        setIsLoading(false)
        return
      }

      // FIRST PRIORITY: Check for regular subscription in subscriptions collection
      const subscription = await subscriptionService.getUserSubscription(user.uid)
      
      if (subscription) {
        // User has subscription data - this takes precedence over trial
        setUserSubscription(subscription)
        

        
        const trialEndsAt = subscription.trialEndsAt?.toDate()
        const currentPeriodEnd = subscription.currentPeriodEnd?.toDate()

        if (subscription.status === 'trial' && trialEndsAt && trialEndsAt > now) {
          // Active trial subscription
          const daysRemaining = Math.ceil((trialEndsAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
          setTrialDaysRemaining(daysRemaining)
          setIsTrialExpired(false)
          setIsSubscriptionActive(true)
          console.log('PaywallContext: Active trial subscription - access granted')
        } else if (subscription.status === 'trial' && trialEndsAt && trialEndsAt <= now) {
          // Expired trial subscription
          setTrialDaysRemaining(0)
          setIsTrialExpired(true)
          setIsSubscriptionActive(false)
          console.log('PaywallContext: Expired trial subscription - access denied')
        } else if (subscription.status === 'active') {
          // Active paid subscription - trust Paystack's 'active' status
          // (currentPeriodEnd may not be set for all subscriptions)
          setTrialDaysRemaining(null)
          setIsTrialExpired(false)
          setIsSubscriptionActive(true)
          console.log('PaywallContext: Active paid subscription - access granted')
        } else if (subscription.status === 'cancelled' || subscription.status === 'non-renewing') {
          // Cancelled or non-renewing subscription - no access regardless of trial status
          setTrialDaysRemaining(null)
          setIsTrialExpired(true)
          setIsSubscriptionActive(false)
          console.log('PaywallContext: Cancelled/non-renewing subscription - access denied')
        } else if (subscription.status === 'attention' || subscription.status === 'payment_failed') {
          // Payment issues - no access until resolved
          setTrialDaysRemaining(null)
          setIsTrialExpired(false)
          setIsSubscriptionActive(false)
          setHasPaymentIssues(true)
          console.log('PaywallContext: Payment issues (status: ' + subscription.status + ') - access denied')
        } else {
          // Other statuses (past_due, etc.) - no access
          setTrialDaysRemaining(null)
          setIsTrialExpired(false)
          setIsSubscriptionActive(false)
          console.log('PaywallContext: Unknown status (' + subscription.status + ') - access denied')
        }
      } else {

        // SECOND PRIORITY: No subscription found - check trial data
        const userProfile = await userService.getUserProfile(user.uid)
        
        if (userProfile?.trial) {
          // User has trial data - check if trial is still active
          const trialEndsAt = userProfile.trial.trial_ends.toDate()
          
          if (trialEndsAt > now) {
            // Trial is still active
            const daysRemaining = Math.ceil((trialEndsAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
            setTrialDaysRemaining(daysRemaining)
            setIsTrialExpired(false)
            setIsSubscriptionActive(true)
            
            // For users who verified their card, they have a Paystack subscription during trial
            // (13-month subscription with R1.50 first month to simulate trial)
            if (!userProfile.trial.skippedVerification) {
              try {
                const { subscriptionService } = await import('../services/firestore')
                const paystackCheck = await subscriptionService.checkPaystackSubscription(user.uid)
                
                // Determine plan type from plan name (e.g., "CLUB 3" -> 'club', "Individual" -> 'individual')
                const planType = userProfile.trial.planName?.toLowerCase().includes('club') ? 'club' : 'individual';
                const courts = planType === 'club' && userProfile.trial.planName 
                  ? parseInt(userProfile.trial.planName.match(/(\d+)/)?.[1] || '1') 
                  : undefined;
                
                // Create subscription object with trial status but include Paystack subscription ID
                setUserSubscription({
                  userId: user.uid,
                  planType: planType,
                  status: 'trial',
                  trialEndsAt: userProfile.trial.trial_ends,
                  currentPeriodStart: userProfile.trial.started,
                  currentPeriodEnd: userProfile.trial.trial_ends,
                  pricePerMonth: paystackCheck.subscriptionData?.amount || 4900,
                  courts: courts,
                  autoRenew: true,
                  createdAt: userProfile.trial.started,
                  updatedAt: userProfile.trial.started,
                  paystackSubscriptionId: paystackCheck.subscriptionData?.subscription_code
                })
              } catch (error) {
                console.error('PaywallContext: Error checking Paystack subscription for trial user:', error)
                // Determine plan type from plan name (e.g., "CLUB 3" -> 'club', "Individual" -> 'individual')
                const planType = userProfile.trial.planName?.toLowerCase().includes('club') ? 'club' : 'individual';
                const courts = planType === 'club' && userProfile.trial.planName 
                  ? parseInt(userProfile.trial.planName.match(/(\d+)/)?.[1] || '1') 
                  : undefined;
                
                // Fallback to trial without Paystack subscription ID
                setUserSubscription({
                  userId: user.uid,
                  planType: planType,
                  status: 'trial',
                  trialEndsAt: userProfile.trial.trial_ends,
                  currentPeriodStart: userProfile.trial.started,
                  currentPeriodEnd: userProfile.trial.trial_ends,
                  pricePerMonth: undefined,
                  courts: courts,
                  autoRenew: false,
                  createdAt: userProfile.trial.started,
                  updatedAt: userProfile.trial.started
                })
              }
            } else {
              // Determine plan type from plan name (e.g., "CLUB 3" -> 'club', "Individual" -> 'individual')
              const planType = userProfile.trial.planName?.toLowerCase().includes('club') ? 'club' : 'individual';
              const courts = planType === 'club' && userProfile.trial.planName 
                ? parseInt(userProfile.trial.planName.match(/(\d+)/)?.[1] || '1') 
                : undefined;
              
              // User skipped verification - no Paystack subscription during trial
              setUserSubscription({
                userId: user.uid,
                planType: planType,
                status: 'trial',
                trialEndsAt: userProfile.trial.trial_ends,
                currentPeriodStart: userProfile.trial.started,
                currentPeriodEnd: userProfile.trial.trial_ends,
                pricePerMonth: undefined,
                courts: courts,
                autoRenew: false,
                createdAt: userProfile.trial.started,
                updatedAt: userProfile.trial.started
              })
            }
          } else {
            // Trial has expired - check if user verified their card
            if (userProfile.trial.skippedVerification) {
              // User didn't verify card - they need to manually upgrade
              console.log('PaywallContext: User skipped verification - trial expired, needs manual upgrade')
              setTrialDaysRemaining(0)
              setIsTrialExpired(true)
              setIsSubscriptionActive(false)
              setUserSubscription(null)
            } else {
              // User verified card - they have a Paystack subscription (13-month with R1.50 first month)
              // Check if the Paystack subscription is still active
              console.log('PaywallContext: User verified card - checking Paystack subscription')
              try {
                const { subscriptionService } = await import('../services/firestore')
                const paystackCheck = await subscriptionService.checkPaystackSubscription(user.uid)
                
                if (paystackCheck.hasActiveSubscription) {
                  // User has active Paystack subscription - they're good to go
                  console.log('PaywallContext: User has active Paystack subscription')
                  setTrialDaysRemaining(null)
                  setIsTrialExpired(false)
                  setIsSubscriptionActive(true)
                  // Determine plan type from plan name (e.g., "CLUB 3" -> 'club', "Individual" -> 'individual')
                  const planType = userProfile.trial.planName?.toLowerCase().includes('club') ? 'club' : 'individual';
                  const courts = planType === 'club' && userProfile.trial.planName 
                    ? parseInt(userProfile.trial.planName.match(/(\d+)/)?.[1] || '1') 
                    : undefined;
                  
                  // Create subscription object from Paystack data
                  setUserSubscription({
                    userId: user.uid,
                    planType: planType,
                    status: 'active',
                    trialEndsAt: userProfile.trial.trial_ends,
                    currentPeriodStart: userProfile.trial.started,
                    currentPeriodEnd: userProfile.trial.trial_ends,
                    pricePerMonth: paystackCheck.subscriptionData?.amount || 4900,
                    courts: courts,
                    autoRenew: true,
                    createdAt: userProfile.trial.started,
                    updatedAt: userProfile.trial.started,
                    paystackSubscriptionId: paystackCheck.subscriptionData?.subscription_code
                  })
                } else {
                  // User verified card but no active subscription - they need to upgrade
                  console.log('PaywallContext: User verified card but no active subscription - needs upgrade')
                  setTrialDaysRemaining(0)
                  setIsTrialExpired(true)
                  setIsSubscriptionActive(false)
                  setUserSubscription(null)
                }
              } catch (error) {
                console.error('PaywallContext: Error checking Paystack subscription:', error)
                // On error, assume user needs to upgrade
                setTrialDaysRemaining(0)
                setIsTrialExpired(true)
                setIsSubscriptionActive(false)
                setUserSubscription(null)
              }
            }
          }
        } else {
          // No trial data and no subscription - user needs to subscribe
          setTrialDaysRemaining(null)
          setIsTrialExpired(false)
          setIsSubscriptionActive(false)
          setUserSubscription(null)
        }
      }

      // Check for payment issues (only for non-trial users)
      // Trial users shouldn't see payment failure warnings
      const isInActiveTrial = trialDaysRemaining !== null && trialDaysRemaining > 0
      
      if (!isInActiveTrial) {
        try {
          const paymentFailureStatus = await subscriptionService.checkPaymentFailureStatus()
          setHasPaymentIssues(paymentFailureStatus.hasPaymentIssues)
          
          if (paymentFailureStatus.hasPaymentIssues && userSubscription) {
            // No grace period - immediately restrict access on payment failure
            setGracePeriodDays(null)
            setIsSubscriptionActive(false)
          } else {
            setGracePeriodDays(null)
          }
        } catch (error) {
          console.error('Error checking payment failure status:', error)
          setHasPaymentIssues(false)
          setGracePeriodDays(null)
        }
      } else {
        // User is in active trial - no payment issues to check
        setHasPaymentIssues(false)
        setGracePeriodDays(null)
      }
    } catch (error) {
      console.error('Error checking subscription status:', error)
      // Default to requiring subscription on error
      setIsSubscriptionActive(false)
    } finally {
      setIsLoading(false)
    }
  }

  const refreshSubscription = async () => {
    await checkSubscriptionStatus()
  }

  useEffect(() => {
    if (isAuthenticated && user?.uid) {
      checkSubscriptionStatus()
    } else if (!isAuthenticated) {
      // Only set loading to false if user is definitely not authenticated
      setIsLoading(false)
    }
    // If isAuthenticated is true but user.uid is not ready yet, keep loading
  }, [isAuthenticated, user?.uid])

  const value: PaywallContextType = {
    isPaywallEnabled,
    isLoading,
    userSubscription,
    trialDaysRemaining,
    isTrialExpired,
    isSubscriptionActive,
    hasPaymentIssues,
    gracePeriodDays,
    checkSubscriptionStatus,
    refreshSubscription
  }

  return (
    <PaywallContext.Provider value={value}>
      {children}
    </PaywallContext.Provider>
  )
} 